/* oxlint-disable typescript/no-unnecessary-condition, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access */
import Service, { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';
import window from 'ember-window-mock';

import ENV from 'register-partners-js/config/environment';

const QONTO_JS_VERSION = ENV.qontoAppVersion;

export class NetworkManagerError extends Error {
  globalErrors?: boolean;
  url: string;
  status: number;
  headers: Record<string, string>;
  method: string;
  responseJSON: Record<string, unknown>;
  responseText: string;
  errors?: unknown;
  data: Record<string, unknown>;

  constructor({
    globalErrors,
    url,
    status,
    responseJSON,
    responseText,
    headers,
    method,
  }: {
    globalErrors?: boolean;
    url: string;
    status: number;
    responseJSON: Record<string, unknown>;
    responseText: string;
    headers: Record<string, string>;
    method: string;
  }) {
    super(`failed to fetch ${url}`);
    this.name = 'NetworkManagerError';

    this.globalErrors = globalErrors;
    this.url = url;
    this.status = status;
    this.headers = headers;
    this.method = method;
    this.responseJSON = responseJSON;
    this.responseText = responseText;
    this.data = this.responseJSON;
  }
}

export function isNetworkManagerError(error: unknown): error is NetworkManagerError {
  return error instanceof NetworkManagerError;
}

export default class NetworkManager extends Service {
  // @ts-expect-error -- service is not fully typed yet
  @service deviceManager;
  // @ts-expect-error -- service is not fully typed yet
  @service intl;

  @tracked _scaHeader: { token: string; header: string } | null = null;

  set scaHeader(params: { token: string; header: string } | null) {
    if (params?.token && params?.header) {
      this._scaHeader = { token: params.token, header: params.header };
    } else {
      this._scaHeader = null;
    }
  }

  @waitFor
  async rawRequest(url: string, options: RequestInit = {}) {
    // oxlint-disable-next-line typescript/no-misused-spread
    let headers = { ...options.headers, ...this.requestHeaders };
    const requestOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include',
    };

    let res = await fetch(url, requestOptions);
    if (!res.ok) {
      let method = requestOptions.method ?? 'GET';

      let text = await res.text();
      let responseJSON: Record<string, unknown>;
      try {
        responseJSON = JSON.parse(text);
      } catch {
        responseJSON = {};
      }

      throw new NetworkManagerError({
        url,
        status: res.status,
        // oxlint-disable-next-line typescript/consistent-type-assertions
        headers: headers as Record<string, string>,
        // oxlint-disable-next-line typescript/consistent-type-assertions
        method: method as string,
        responseJSON,
        responseText: text,
      });
    }

    return res;
  }

  @waitFor
  async request(url: string, options?: RequestInit) {
    try {
      let res = await this.rawRequest(url, options);
      if (res.status === 204) return '';
      if (options?.method?.toUpperCase() === 'HEAD') return res;
      // oxlint-disable-next-line typescript/no-unsafe-return
      return res.json();
    } finally {
      if (this._scaHeader) this.scaHeader = null;
    }
  }

  get requestHeaders(): Record<string, string> {
    let headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Qonto-Device-Type': 'browser',
      'X-Qonto-Locale': this.intl.primaryLocale,
      'X-Qonto-Referrer': window.location.href,
    };

    if (this._scaHeader?.header && this._scaHeader?.token) {
      headers[this._scaHeader?.header] = this._scaHeader?.token;
    }

    if (this.deviceManager.id) {
      headers['X-Qonto-Device'] = this.deviceManager.id;
    }

    if (QONTO_JS_VERSION) {
      headers['X-Qonto-Platform-Version'] = QONTO_JS_VERSION;
    }

    if (ENV.isStaging) {
      // @ts-expect-error -- property may not exist in all environments
      headers['X-Qonto-Staging-Token'] = ENV.stagingAccessToken;
    }

    return headers;
  }
}

declare module '@ember/service' {
  interface Registry {
    networkManager: NetworkManager;
    'network-manager': NetworkManager;
  }
}
